/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 *
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useState, useEffect } from 'react'

import {
  ApprovalState,
  requiredText,
  useCheckOperation,
  useCoordinator,
  useGetSettings,
} from '@builddocs/data-management'
import { WorkType } from '@builddocs/data-management'
import { useDisclosure } from '@builddocs/system'
import TextareaAutosize from '@mui/base/TextareaAutosize'
import { LoadingButton } from '@mui/lab'
import {
  Dialog,
  Button,
  Stack,
  Typography,
  DialogContent,
  FormControl,
  FormLabel,
  TextField,
  FormHelperText,
  DialogActions,
  DialogTitle,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { addDays } from 'date-fns'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { ConfirmDialog } from './ConfirmDialog'

interface IDeviationFields {
  information: string
  problemDescription: string
  deadline: string
  externalEntityInfo: string
}

interface IDeviationDialogProps {
  title?: string
  subtitle?: string
  onSubmit: (
    body: IDeviationFields,
    additionalInfo: {
      id: string
      workType?: WorkType
      aosrIds?: string[]
      secondaryActIds?: string[]
    },
    onSuccess: () => void,
  ) => void
  additionalInfo: {
    id: string
    workType?: WorkType
    aosrIds?: string[]
    secondaryActIds?: string[]
    entityType?: 'stamp' | 'act'
  }
  approvalStatus?: ApprovalState
  initialInformation?: string
  complect?: string
  countActs?: number
  documentType: string
  isDisabled?: boolean
}

export const DeviationDialog = (props: IDeviationDialogProps): JSX.Element => {
  const {
    title,
    subtitle,
    onSubmit,
    additionalInfo,
    complect,
    countActs,
    documentType,
    isDisabled,
    approvalStatus,
  } = props
  const { projectId } = useCoordinator()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isOpenConfirmModal,
    onOpen: onOpenConfirmModal,
    onClose: onCloseConfirmModal,
  } = useDisclosure()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const queryClient = useQueryClient()

  const { data: setting } = useGetSettings({
    projectId,
  })
  const { checkOperation } = useCheckOperation()

  const { handleSubmit, formState, errors, control } =
    useForm<IDeviationFields>()

  const submitDeviation: SubmitHandler<IDeviationFields> = data => {
    if (formState.isSubmitting || isLoading) return

    setIsLoading(true)

    onSubmit(
      { ...data, externalEntityInfo: documentType },
      additionalInfo,
      () => {
        setIsLoading(false)
        onClose()
        const { aosrIds = [], secondaryActIds = [] } = additionalInfo
        const ids = [...aosrIds, ...secondaryActIds]

        ids.forEach(id =>
          queryClient.invalidateQueries(['comments', 'byEntityId', id]),
        )
      },
    )
  }

  const handleConfirm = () => {
    onCloseConfirmModal()
    handleSubmit(submitDeviation)()
  }

  useEffect(() => {
    if (isOpen) setIsLoading(false)
  }, [isOpen])

  const handleRejectOpenClick = () => {
    console.log('handleRejectOpenClick', documentType)
    console.log('additionalInfo', additionalInfo)
    if (additionalInfo.entityType === 'stamp') {
      checkOperation({
        operationId: 'STAMP_REJECT',
        operationCallback: onOpen,
      })
    }
    if (additionalInfo.entityType === 'act') {
      console.log('approvalStatus', approvalStatus)
      if (
        approvalStatus === undefined ||
        approvalStatus === ApprovalState.APPROVAL
      ) {
        checkOperation({
          operationId: 'REJECT_ACT_ON_APPROVAL',
          operationCallback: onOpen,
        })
      }

      if (approvalStatus === ApprovalState.SIGNING) {
        checkOperation({
          operationId: 'REJECT_ACT_ON_SIGNING',
          operationCallback: onOpen,
        })
      }
    }
  }

  return (
    <>
      {complect === 'inspect' ? (
        <Button
          key={1}
          color="rejected"
          disabled={!countActs || isDisabled}
          id="deviation-dialog-button-open-modal"
          size="small"
          onClick={handleRejectOpenClick}
        >
          {countActs
            ? `Отклонить акты (выбрано: ${countActs})`
            : `Выберите акты для отклонения`}
        </Button>
      ) : (
        <Button
          color="rejected"
          disabled={isDisabled}
          id="deviation-dialog-button-open-modal-alter"
          size="small"
          onClick={handleRejectOpenClick}
        >
          Отклонить
        </Button>
      )}
      <ConfirmDialog
        isOpen={isOpenConfirmModal && approvalStatus === ApprovalState.SIGNING}
        onCancel={onCloseConfirmModal}
        onConfirm={handleConfirm}
      />
      <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          <form id="deviation-dialog">
            {subtitle && <Typography mb={4}>{subtitle}</Typography>}
            <Stack spacing={2}>
              <Controller
                control={control}
                defaultValue=""
                name="problemDescription"
                render={props => (
                  <FormControl
                    fullWidth
                    error={!!errors?.problemDescription}
                    id="place"
                  >
                    <FormLabel>Выявленные недостатки</FormLabel>
                    <TextareaAutosize
                      minRows={2}
                      style={{
                        border: `1px solid ${
                          errors.problemDescription ? 'red' : 'lightgray'
                        }`,
                        borderRadius: 1.25,
                      }}
                      {...props}
                    />
                    <FormHelperText>
                      {errors?.problemDescription?.message}
                    </FormHelperText>
                  </FormControl>
                )}
                rules={{ required: requiredText }}
              />
              <Controller
                control={control}
                defaultValue={
                  setting?.remarkingDaysMax
                    ? addDays(new Date(), setting?.remarkingDaysMax)
                    : null
                }
                name="deadline"
                render={props => (
                  <DatePicker
                    {...props}
                    clearable
                    label="Срок устранения"
                    mask="__.__.____"
                    minDate={new Date()}
                    renderInput={({ error, ...params }) => (
                      <TextField
                        error={!!errors.deadline}
                        helperText={errors.deadline?.message}
                        {...params}
                      />
                    )}
                  />
                )}
                rules={{
                  required: setting?.remarkingDaysMax
                    ? 'Должно быть заполнено'
                    : undefined,
                }}
              />
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            aria-label="close"
            disabled={formState.isSubmitting || isLoading}
            onClick={onClose}
          >
            Отмена
          </Button>
          <LoadingButton
            loading={formState.isSubmitting || isLoading}
            variant="contained"
            onClick={
              approvalStatus === ApprovalState.SIGNING
                ? onOpenConfirmModal
                : handleSubmit(submitDeviation)
            }
          >
            Сохранить
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
